/* CSV/TXT punch import (drop zone → importPunches → result summary). */
import { useId, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import type { IngestResult } from "../../../data/types";
import { useImportPunches } from "../../../data/hooks";
import { Alert, Button, Icon, errMsg, useToast } from "../../../ui";
import { useI18n } from "../../../i18n";
import { downloadText } from "../../../lib/format";

export function readText(f: Blob): Promise<string> {
  if (typeof f.text === "function") return f.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error);
    r.readAsText(f);
  });
}

export const SAMPLE_CSV = "UserID,DateTime,Status,Verify\n101,2026-10-03 07:52:10,in,face\n101,2026-10-03 17:04:44,out,face\n";

export function IngestSummary({ r }: { r: IngestResult }) {
  const { L } = useI18n();
  return (
    <div className="stack" data-testid="ingest-result">
      <div className="ingest">
        <div><b data-testid="ingest-accepted">{r.accepted}</b><span>{L("Accepted", "مقبولة")}</span></div>
        <div><b data-testid="ingest-duplicates">{r.duplicates}</b><span>{L("Duplicates skipped", "مكررة (تم تجاهلها)")}</span></div>
        <div><b data-testid="ingest-unmatched">{r.unmatched}</b><span>{L("Unmatched users", "مستخدمون غير مرتبطين")}</span></div>
        <div><b data-testid="ingest-rejected">{r.rejected}</b><span>{L("Rejected rows", "صفوف مرفوضة")}</span></div>
      </div>
      {r.unmatched > 0 && (
        <Alert tone="warn" tint title={L("Some punches aren't linked to an employee yet", "بعض البصمات غير مرتبطة بموظف بعد")}
          action={<Link className="btn sm" to="/biometric/unmatched">{L("Link users", "ربط المستخدمين")}</Link>} />
      )}
      {r.errors.length > 0 && (
        <details>
          <summary className="sm">{L(`${r.errors.length} row problems`, `${r.errors.length} مشكلة في الصفوف`)}</summary>
          <pre className="codeblock mt" dir="ltr">{r.errors.slice(0, 10).join("\n")}</pre>
        </details>
      )}
    </div>
  );
}

export function FileImport({ deviceId, onDone }: { deviceId: string; onDone?: (r: IngestResult) => void }) {
  const { L } = useI18n();
  const toast = useToast();
  const id = useId();
  const imp = useImportPunches();
  const [over, setOver] = useState(false);
  const [name, setName] = useState("");
  const [res, setRes] = useState<IngestResult | null>(null);

  const handle = async (f: File | undefined) => {
    if (!f) return;
    setName(f.name);
    let csv = "";
    try { csv = await readText(f); } catch (e) { toast(errMsg(e), "bad"); return; }
    imp.mutate({ deviceId, csv }, {
      onSuccess: (r) => { setRes(r); onDone?.(r); toast(L(`${r.accepted} punches imported`, `تم استيراد ${r.accepted} بصمة`)); },
      onError: (e) => toast(errMsg(e), "bad"),
    });
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setOver(false); void handle(e.dataTransfer.files?.[0]); };

  return (
    <div className="stack" data-testid="file-import">
      <div className={"drop" + (over ? " over" : "")} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop} data-testid="dropzone">
        <Icon name="upload" />
        <b>{L("Drop the exported attendance file here", "أفلت ملف الحضور المصدّر هنا")}</b>
        <span className="xs">{L("CSV, TXT or tab-separated · columns are detected automatically (user ID, date/time, in/out)", "CSV أو TXT أو مفصول بعلامات جدولة · تُكتشف الأعمدة تلقائيًا (رقم المستخدم، التاريخ والوقت، دخول/خروج)")}</span>
        <label className="btn sm" htmlFor={id}>{imp.isPending ? <span className="spin" aria-hidden="true" /> : <Icon name="file" />}<span>{L("Choose file", "اختيار ملف")}</span></label>
        <input id={id} type="file" accept=".csv,.txt,.tsv,text/csv,text/plain" className="sr" aria-label={L("Attendance file", "ملف الحضور")} data-testid="import-file" onChange={(e) => { void handle(e.target.files?.[0]); e.target.value = ""; }} />
        {name && <span className="xs muted" dir="ltr">{name}</span>}
      </div>
      <div><Button variant="ghost sm" icon="download" onClick={() => downloadText("rukn-punches-sample.csv", SAMPLE_CSV)} data-testid="btn-sample-csv">{L("Download a sample file", "تنزيل ملف نموذجي")}</Button></div>
      {res && <IngestSummary r={res} />}
    </div>
  );
}
